import React, { useMemo, useState } from "react";
import { preflightExportRerun, triggerExportRerun, isConfirmationRequiredError } from "./services/exportJobsService";
import { useModalMotion } from "../../components/motion/useModalMotion";
import { ModalShell } from "../../components/motion/ModalShell";

// Support-only portal modal to trigger a manual re-run of real
// assisted_conversions data for an explicit past [window_from, window_to)
// range (074/DEV-2036 follow-up). Modeled on ExecutionLedgerModal.jsx's
// portal shape. Two dates ("From"/"To") give a half-open day window
// (window_to is exclusive, matching the scheduled run's own predicate).
//
// Flow: pick a range → "Preview" calls preflightExportRerun() (read-only,
// no send) to render the advisory list → the operator must tick the
// confirm checkbox before "Trigger Re-Run" is enabled, regardless of
// whether any advisory is present (spec: "operator MUST explicitly confirm
// before any send proceeds") → triggerExportRerun() is the only call that
// creates a export_job_reruns row; a CONFIRMATION_REQUIRED response (the
// operator confirmed locally, but the RPC's own re-check still requires it,
// e.g. because the window changed between preview and submit) is surfaced
// as a plain error rather than silently retried.
export function RerunExportModal({ job, onClose, onTriggered }) {
  const [windowFrom, setWindowFrom] = useState("");
  const [windowTo, setWindowTo] = useState("");
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [preflight, setPreflight] = useState(null); // { blocking, advisories } | null
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const motion = useModalMotion(onClose, { dismissible: !submitting, closeOnBackdrop: true });
  const { requestClose } = motion;

  // Only column-type mappings carry a target_key that can receive a forced
  // random_suffix override — static mappings have no source data to
  // randomize. Reading straight from the job's field_mappings prop, never
  // mutating it: the checkboxes select the per-run override, which the
  // trigger call carries as its own argument and never writes back here.
  const columnTargetKeys = useMemo(
    () => (job?.field_mappings || []).filter((m) => m.type === "column").map((m) => m.target_key),
    [job?.field_mappings]
  );

  const toWindowIso = (dateStr) => (dateStr ? `${dateStr}T00:00:00.000Z` : null);

  const canPreview = !!windowFrom && !!windowTo;
  const hasBlocking = (preflight?.blocking?.length || 0) > 0;
  const canSubmit = !!preflight && !hasBlocking && confirmed && !submitting;

  const toggleKey = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handlePreview = async () => {
    if (!canPreview) return;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreflight(null);
    setConfirmed(false);
    const { data, error } = await preflightExportRerun(job.id, toWindowIso(windowFrom), toWindowIso(windowTo));
    setPreviewLoading(false);
    if (error) { setPreviewError(error.message); return; }
    setPreflight(data);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    const { data, error } = await triggerExportRerun(
      job.id,
      toWindowIso(windowFrom),
      toWindowIso(windowTo),
      Array.from(selectedKeys),
      confirmed
    );
    setSubmitting(false);
    if (error) {
      setSubmitError(error.message);
      if (isConfirmationRequiredError(error)) setConfirmed(false);
      return;
    }
    onTriggered?.(data);
    requestClose();
  };

  return (
    <ModalShell
      motion={motion}
      labelledBy="rerun-export-title"
      maxWidth={560}
      panelStyle={{ overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "85vh" }}
    >
        <div style={{ padding: "22px 24px 16px", borderBottom: "1px solid #F0F0F0", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 11, color: "#7b7b7b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Support Only</div>
            <div id="rerun-export-title" style={{ fontWeight: 600, color: "#151515", fontSize: 24, fontFamily: "'Clash Display Variable','Clash Display',sans-serif" }}>Re-run dates</div>
          </div>
          <button onClick={requestClose} aria-label="Close" style={{ background: "none", border: "none", color: "#626262", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ padding: 24, overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "#626262" }}>
              From
              <input
                type="date"
                aria-label="From"
                value={windowFrom}
                onChange={(e) => { setWindowFrom(e.target.value); setPreflight(null); setConfirmed(false); }}
                style={{ padding: "7px 10px", border: "1px solid #CFCFCF", borderRadius: 8, fontSize: 13 }}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "#626262" }}>
              To
              <input
                type="date"
                aria-label="To"
                value={windowTo}
                onChange={(e) => { setWindowTo(e.target.value); setPreflight(null); setConfirmed(false); }}
                style={{ padding: "7px 10px", border: "1px solid #CFCFCF", borderRadius: 8, fontSize: 13 }}
              />
            </label>
          </div>

          {columnTargetKeys.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: "#626262", marginBottom: 6 }}>
                Force distinct order IDs for this run only (never saved to the job's field mappings):
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {columnTargetKeys.map((key) => (
                  <label key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#151515" }}>
                    <input type="checkbox" aria-label={key} checked={selectedKeys.has(key)} onChange={() => toggleKey(key)} />
                    {key}
                  </label>
                ))}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={handlePreview}
            disabled={!canPreview || previewLoading}
            style={{ padding: "9px 16px", background: "#FAFAFA", border: "1px solid #E2E2E2", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#151515", cursor: canPreview ? "pointer" : "not-allowed", alignSelf: "flex-start" }}
          >
            {previewLoading ? "Checking…" : "Preview"}
          </button>

          {previewError && <div style={{ fontSize: 13, color: "#e53935", background: "#FFF5F5", borderRadius: 6, padding: "6px 10px" }}>{previewError}</div>}

          {preflight && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#151515", background: "#FAFAFA", border: "1px solid #E2E2E2", borderRadius: 6, padding: "8px 10px" }}>
                {/* A count of zero is meaningful — always render an explicit
                    number, never suppress this line, so an operator seeing
                    "0 conversions selected" gets the signal that a re-run
                    would do nothing. */}
                {preflight.conversion_count ?? 0} conversions selected
              </div>
              {[...(preflight.blocking || []), ...(preflight.advisories || [])].map((item, i) => {
                const isBlocking = (preflight.blocking || []).includes(item);
                return (
                  <div
                    key={`${item.code}-${i}`}
                    style={{
                      fontSize: 13,
                      color: isBlocking ? "#e53935" : "#8a5a00",
                      background: isBlocking ? "#FFF5F5" : "#FFF8E6",
                      border: `1px solid ${isBlocking ? "#fca5a5" : "#F3D98B"}`,
                      borderRadius: 6,
                      padding: "8px 10px",
                    }}
                  >
                    <div>{item.message}</div>
                    {item.provisional && (
                      <span style={{ display: "inline-block", marginTop: 4, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "#8a5a00", background: "#FFF3CC", borderRadius: 20, padding: "1px 7px" }}>
                        Unconfirmed
                      </span>
                    )}
                  </div>
                );
              })}

              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#151515" }}>
                <input
                  type="checkbox"
                  aria-label="Confirm"
                  checked={confirmed}
                  disabled={hasBlocking}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                I have reviewed the re-run range and advisories above, including the risk that this send may be rejected as a duplicate or fail an age/association-window check, and confirm the send.
              </label>
            </div>
          )}

          {submitError && <div style={{ fontSize: 13, color: "#e53935", background: "#FFF5F5", borderRadius: 6, padding: "6px 10px" }}>{submitError}</div>}
        </div>

        <div style={{ padding: "16px 24px", borderTop: "1px solid #F0F0F0", flexShrink: 0, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={requestClose} style={{ padding: "9px 16px", background: "transparent", border: "1px solid #E2E2E2", borderRadius: 8, fontSize: 12, color: "#626262", cursor: "pointer" }}>Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              padding: "9px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600,
              border: "none",
              background: canSubmit ? "#F90091" : "#F5F5F5",
              color: canSubmit ? "#fff" : "#B8B8B8",
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {submitting ? "Triggering…" : "Trigger Re-Run"}
          </button>
        </div>
    </ModalShell>
  );
}
